import { setAuthHeader } from "@/apis/client";
import { register } from "@/apis/v1/user";
import CustomLoadingButton from "@/components/Button/CustomLoadingButton";
import CustomInput from "@/components/Form/InputFields/CustomInput";
import { TOKEN_KEY, USER_KEY } from "@/constants";
import { useUserContext } from "@/context/UserContext";
import routes from "@/routes";
import { yupResolver } from "@hookform/resolvers/yup";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import {
  advertiserValidationSchema,
  agencyValidationSchema,
  IAdvertiserRegister,
  IAgencyRegister,
} from "./validation";

interface RegistrationFormProps {
  registrationType: "advertiser" | "agency";
  onBack: () => void;
}

type Error = {
  errMsg: string;
};

export default function RegistrationForm({
  registrationType,
  onBack,
}: RegistrationFormProps) {
  const { setUser, setToken } = useUserContext();

  const {
    mutateAsync: registerMutate,
    isLoading: isRegisterLoading,
    error: registerError,
  } = useMutation(register);

  const isAdvertiser = registrationType === "advertiser";
  const validationSchema = isAdvertiser
    ? advertiserValidationSchema
    : agencyValidationSchema;

  const defaultValues = isAdvertiser
    ? {
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
        companyName: "",
        domain: "",
      }
    : {
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
        companyName: "",
      };

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<IAdvertiserRegister | IAgencyRegister>({
    defaultValues,
    resolver: yupResolver(validationSchema),
  });

  async function onFormSubmit(data: IAdvertiserRegister | IAgencyRegister) {
    const { confirmPassword, ...registrationData } = data;
    if (!isAdvertiser) return;
    const res = await registerMutate({
      ...registrationData,
      registrationType,
    });

    window.localStorage.setItem(TOKEN_KEY, res.token);
    window.localStorage.setItem(USER_KEY, JSON.stringify(res.user));
    setAuthHeader(res.token);
    setUser(res.user);
    setToken(res.token);
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        height: "100%",
        paddingTop: 8,
      }}
    >
      <Box>
        <FormHelperText error>
          {(registerError as Error)?.errMsg}
        </FormHelperText>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 2,
          width: "100%",
          maxWidth: 400,
        }}
      >
        <CustomLoadingButton
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          variant="text"
          sx={{ mr: 2 }}
          title="Back"
          isLoading={false}
          disabled={isRegisterLoading}
        />
        <Box sx={{ flex: 1, textAlign: "center" }}>
          <Avatar sx={{ m: "0 auto", mb: 1, bgcolor: "secondary.main" }}>
            <PersonAddOutlinedIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            Sign up as {isAdvertiser ? "Advertiser" : "Agency"}
          </Typography>
        </Box>
      </Box>
      <Box
        component="form"
        onSubmit={handleSubmit(onFormSubmit)}
        sx={{ mt: 3, width: "100%", maxWidth: 400 }}
      >
        <CustomInput
          name="username"
          label="Username"
          control={control}
          error={errors?.username?.message}
        />
        <CustomInput
          name="email"
          label="Email Address"
          control={control}
          error={errors?.email?.message}
        />
        <CustomInput
          name="companyName"
          label="Company Name"
          control={control}
          error={errors?.companyName?.message}
        />
        {isAdvertiser && (
          <CustomInput
            name="domain"
            label="Domain"
            control={control}
            error={(errors as any)?.domain?.message}
          />
        )}
        <CustomInput
          name="password"
          label="Password"
          inputType="password"
          control={control}
          error={errors?.password?.message}
        />
        <CustomInput
          name="confirmPassword"
          label="Confirm Password"
          inputType="password"
          control={control}
          error={errors?.confirmPassword?.message}
        />

        <CustomLoadingButton
          type="submit"
          title="Create Account"
          isLoading={isRegisterLoading}
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
          disabled={isRegisterLoading}
        />
        <Grid container justifyContent="center">
          <Grid item>
            <Link
              href={routes.LOGIN}
              variant="body2"
              sx={{
                pointerEvents: isRegisterLoading ? "none" : "auto",
                opacity: isRegisterLoading ? 0.5 : 1,
              }}
            >
              Already have an account? Sign in
            </Link>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
